import { ArrowLeftIcon, ArrowRightIcon, KeyRoundIcon, Loader2Icon, MailIcon, ShieldCheckIcon, SmartphoneIcon } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FormError } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, errorMessage } from '@/lib/api';
import { useBranding } from '@/lib/branding';
import type { MfaMethodDto, MfaMethodType, MfaPendingResponse, SessionResponse, TotpEnrollment } from '@/lib/types';
import { cn } from '@/lib/utils';
import { RecoveryCodes } from './RecoveryCodes';

/**
 * The second step of signing in. `pending` came back from the first step (password, one-time code, or the
 * SSO redirect). Two stages: verify (they have a method) or enroll (policy requires one, they have none).
 */
export function MfaStep({ pending, onSession, onBack }: { pending: MfaPendingResponse; onSession: (session: SessionResponse) => Promise<void> | void; onBack: () => void }) {
  return pending.mfa.stage === 'verify' ? <VerifyStage pending={pending} onSession={onSession} onBack={onBack} /> : <EnrollStage pending={pending} onSession={onSession} onBack={onBack} />;
}

const TYPE_ICON: Record<MfaMethodType, typeof KeyRoundIcon> = { totp: SmartphoneIcon, sms: SmartphoneIcon, email: MailIcon };
const TYPE_LABEL: Record<MfaMethodType, string> = { totp: 'Authenticator app', sms: 'Text message', email: 'Email' };

export function methodTitle(m: MfaMethodDto): string {
  return m.label && m.label !== TYPE_LABEL[m.type] ? m.label : TYPE_LABEL[m.type];
}

function RememberDevice({ days, checked, onChange }: { days: number; checked: boolean; onChange: (v: boolean) => void }) {
  if (days <= 0) return null;
  return (
    <div className="flex items-center gap-2.5">
      <Checkbox id="mfa-remember" checked={checked} onCheckedChange={(v) => onChange(v === true)} />
      <Label htmlFor="mfa-remember" className="text-muted-foreground cursor-pointer font-normal">
        Don’t ask again on this device for {days} {days === 1 ? 'day' : 'days'}
      </Label>
    </div>
  );
}

function CodeInput({ id, value, onChange, recovery = false }: { id: string; value: string; onChange: (v: string) => void; recovery?: boolean }) {
  return (
    <Input
      id={id}
      inputMode={recovery ? 'text' : 'numeric'}
      autoComplete="one-time-code"
      autoCapitalize="none"
      spellCheck={false}
      maxLength={recovery ? 12 : 7}
      autoFocus
      placeholder={recovery ? 'xxxxx-xxxxx' : '123 456'}
      className={cn('text-center text-xl', recovery ? 'tracking-[0.15em]' : 'tracking-[0.4em]')}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

// ---------------------------------------------------------------- verify

function VerifyStage({ pending, onSession, onBack }: { pending: MfaPendingResponse; onSession: (s: SessionResponse) => Promise<void> | void; onBack: () => void }) {
  const { mfa } = pending;
  const initial = useMemo(() => mfa.methods.find((m) => m.type === 'totp') ?? mfa.methods[0] ?? null, [mfa.methods]);
  const [method, setMethod] = useState<MfaMethodDto | null>(initial);
  const [recovery, setRecovery] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(false);
  const [sent, setSent] = useState<{ destination: string | null; resendAfterSeconds: number } | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  // Text / email methods need a code sent first.
  useEffect(() => {
    if (!method || method.type === 'totp' || recovery || sent) return;
    void sendCode(method);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method?.id, recovery]);

  async function sendCode(m: MfaMethodDto) {
    setError(null);
    try {
      const res = await api.auth.mfa.challenge(mfa.ticket, m.id);
      setSent({ destination: res.destination, resendAfterSeconds: res.resendAfterSeconds });
      setResendIn(res.resendAfterSeconds);
    } catch (err) {
      setError(errorMessage(err, 'Could not send a code'));
    }
  }

  const pick = (m: MfaMethodDto) => {
    setMethod(m);
    setRecovery(false);
    setChoosing(false);
    setSent(null);
    setCode('');
    setError(null);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!method && !recovery) return;
    setBusy(true);
    setError(null);
    try {
      const session = await api.auth.mfa.verify(mfa.ticket, { methodId: recovery ? 'recovery' : method?.id, code: code.trim(), rememberDevice: remember });
      await onSession(session);
    } catch (err) {
      setError(errorMessage(err, 'That code did not work'));
      setBusy(false);
    }
  };

  if (choosing) {
    return (
      <div className="grid gap-4">
        <p className="text-muted-foreground text-sm">Choose how to verify it’s you.</p>
        <ul className="grid gap-2">
          {mfa.methods.map((m) => {
            const Icon = TYPE_ICON[m.type];
            return (
              <li key={m.id}>
                <button type="button" onClick={() => pick(m)} className="hover:bg-muted/60 flex w-full items-center gap-3 border p-3 text-left text-sm">
                  <Icon className="text-muted-foreground size-4 shrink-0" aria-hidden />
                  <span className="min-w-0">
                    <span className="block font-medium">{methodTitle(m)}</span>
                    {m.destination ? <span className="text-muted-foreground block text-xs">{m.destination}</span> : null}
                  </span>
                </button>
              </li>
            );
          })}
          <li>
            <button
              type="button"
              onClick={() => {
                setRecovery(true);
                setChoosing(false);
                setCode('');
              }}
              className="hover:bg-muted/60 flex w-full items-center gap-3 border p-3 text-left text-sm"
            >
              <KeyRoundIcon className="text-muted-foreground size-4 shrink-0" aria-hidden />
              <span className="font-medium">Use a recovery code</span>
            </button>
          </li>
        </ul>
        <button type="button" onClick={onBack} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 self-start text-sm underline-offset-4 hover:underline">
          <ArrowLeftIcon className="size-3.5" aria-hidden /> Back to sign in
        </button>
      </div>
    );
  }

  const Icon = recovery ? KeyRoundIcon : method ? TYPE_ICON[method.type] : ShieldCheckIcon;
  return (
    <form onSubmit={submit} className="grid gap-5" noValidate>
      <div className="bg-muted/50 text-muted-foreground flex items-start gap-3 border p-3 text-sm">
        <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
        <span>
          {recovery ? (
            <>Enter one of your recovery codes. Each code works once.</>
          ) : method?.type === 'totp' ? (
            <>
              Open <span className="text-foreground font-medium">{methodTitle(method)}</span> and enter the 6-digit code it shows.
            </>
          ) : sent ? (
            <>
              We sent a 6-digit code to <span className="text-foreground font-medium">{sent.destination}</span>.
            </>
          ) : (
            <>Sending a code…</>
          )}
        </span>
      </div>
      <FormError message={error} />
      <Field label={recovery ? 'Recovery code' : 'Verification code'} htmlFor="mfa-code">
        <CodeInput id="mfa-code" value={code} onChange={setCode} recovery={recovery} />
      </Field>
      <RememberDevice days={mfa.rememberDeviceDays} checked={remember} onChange={setRemember} />
      <Button type="submit" size="lg" className="group mt-1 w-full" disabled={busy || code.trim().length < 6}>
        {busy ? (
          <>
            <Loader2Icon className="animate-spin" aria-hidden /> Verifying…
          </>
        ) : (
          <>
            Verify <ArrowRightIcon className="transition-transform group-hover:translate-x-0.5" aria-hidden />
          </>
        )}
      </Button>
      <div className="text-muted-foreground flex flex-wrap items-center justify-between gap-2 text-xs">
        {!recovery && method && method.type !== 'totp' ? (
          <button type="button" onClick={() => void sendCode(method)} disabled={resendIn > 0} className="hover:text-foreground underline-offset-4 hover:underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60">
            {resendIn > 0 ? `Resend code in ${resendIn}s` : 'Resend code'}
          </button>
        ) : (
          <span />
        )}
        <button type="button" onClick={() => setChoosing(true)} className="hover:text-foreground underline-offset-4 hover:underline">
          {mfa.methods.length > 1 || !recovery ? 'Try another way' : 'Use a verification method'}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------- enroll

type EnrollChoice = MfaMethodType | null;

function EnrollStage({ pending, onSession, onBack }: { pending: MfaPendingResponse; onSession: (s: SessionResponse) => Promise<void> | void; onBack: () => void }) {
  const { mfa } = pending;
  const { branding } = useBranding();
  const allowed = (Object.entries(mfa.allowedMethods) as Array<[MfaMethodType, boolean]>).filter(([, on]) => on).map(([t]) => t);
  const [choice, setChoice] = useState<EnrollChoice>(allowed.length === 1 ? (allowed[0] as MfaMethodType) : null);
  const [totp, setTotp] = useState<TotpEnrollment | null>(null);
  const [otpMethodId, setOtpMethodId] = useState<string | null>(null);
  const [otpDestination, setOtpDestination] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [finished, setFinished] = useState<{ session: SessionResponse; recoveryCodes: string[] | null } | null>(null);

  useEffect(() => {
    if (choice !== 'totp' || totp) return;
    setBusy(true);
    api.auth.mfa
      .enrollTotpStart(mfa.ticket)
      .then(setTotp)
      .catch((err) => setError(errorMessage(err, 'Could not start the setup')))
      .finally(() => setBusy(false));
  }, [choice, totp, mfa.ticket]);

  const startOtp = async (e: FormEvent) => {
    e.preventDefault();
    if (!choice || choice === 'totp') return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.auth.mfa.enrollOtpStart(mfa.ticket, choice, otpDestination.trim());
      setOtpMethodId(res.method.id);
      setSentTo(res.method.destination);
    } catch (err) {
      setError(errorMessage(err, 'Could not send a code'));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res =
        choice === 'totp' && totp
          ? await api.auth.mfa.enrollTotpConfirm(mfa.ticket, totp.method.id, code.trim(), remember)
          : otpMethodId
            ? await api.auth.mfa.enrollOtpConfirm(mfa.ticket, otpMethodId, code.trim(), remember)
            : null;
      if (!res) return;
      if (res.recoveryCodes && res.recoveryCodes.length > 0) setFinished({ session: res, recoveryCodes: res.recoveryCodes });
      else await onSession(res);
    } catch (err) {
      setError(errorMessage(err, 'That code did not work'));
    } finally {
      setBusy(false);
    }
  };

  if (finished) {
    return <RecoveryCodes codes={finished.recoveryCodes ?? []} appName={branding.appName} onDone={() => void onSession(finished.session)} doneLabel="Continue to the app" />;
  }

  if (!choice) {
    return (
      <div className="grid gap-4">
        <div className="bg-muted/50 text-muted-foreground flex items-start gap-3 border p-3 text-sm">
          <ShieldCheckIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>
            <span className="text-foreground font-medium">{pending.tenant.name}</span> requires two-step verification. Choose how you want to receive your codes.
          </span>
        </div>
        <ul className="grid gap-2">
          {allowed.map((t) => {
            const Icon = TYPE_ICON[t];
            return (
              <li key={t}>
                <button type="button" onClick={() => setChoice(t)} className="hover:bg-muted/60 flex w-full items-center gap-3 border p-3 text-left text-sm">
                  <Icon className="text-muted-foreground size-4 shrink-0" aria-hidden />
                  <span>
                    <span className="block font-medium">{TYPE_LABEL[t]}</span>
                    <span className="text-muted-foreground block text-xs">
                      {t === 'totp' ? 'Microsoft Authenticator, Google Authenticator, 1Password… Works offline.' : t === 'sms' ? 'A 6-digit code texted to your mobile number.' : 'A 6-digit code emailed to you.'}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        <button type="button" onClick={onBack} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 self-start text-sm underline-offset-4 hover:underline">
          <ArrowLeftIcon className="size-3.5" aria-hidden /> Back to sign in
        </button>
      </div>
    );
  }

  const back = (
    <button
      type="button"
      onClick={() => {
        setChoice(allowed.length === 1 ? choice : null);
        setTotp(null);
        setOtpMethodId(null);
        setSentTo(null);
        setCode('');
        setError(null);
        if (allowed.length === 1) onBack();
      }}
      className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 self-start text-xs underline-offset-4 hover:underline"
    >
      <ArrowLeftIcon className="size-3.5" aria-hidden /> {allowed.length === 1 ? 'Back to sign in' : 'Choose a different method'}
    </button>
  );

  if (choice === 'totp') {
    return (
      <form onSubmit={confirm} className="grid gap-5" noValidate>
        <FormError message={error} />
        {totp ? (
          <div className="grid gap-4 sm:grid-cols-[auto_1fr] sm:items-start">
            <img src={totp.qrDataUrl} alt="QR code for your authenticator app" width={176} height={176} className="bg-white border p-1" />
            <div className="grid gap-2 text-sm">
              <p className="font-medium">1. Scan this code with your authenticator app</p>
              <p className="text-muted-foreground text-xs">
                Or enter this key manually: <span className="text-foreground font-mono tracking-wider select-all">{totp.secretFormatted}</span>
              </p>
              <p className="mt-1 font-medium">2. Enter the 6-digit code the app shows</p>
              <CodeInput id="mfa-enroll-code" value={code} onChange={setCode} />
            </div>
          </div>
        ) : (
          <div className="text-muted-foreground flex items-center gap-2 text-sm">
            <Loader2Icon className="size-4 animate-spin" aria-hidden /> Preparing your setup…
          </div>
        )}
        <RememberDevice days={mfa.rememberDeviceDays} checked={remember} onChange={setRemember} />
        <Button type="submit" size="lg" className="group mt-1 w-full" disabled={busy || !totp || code.trim().length < 6}>
          {busy ? (
            <>
              <Loader2Icon className="animate-spin" aria-hidden /> Checking…
            </>
          ) : (
            <>
              Turn on and sign in <ArrowRightIcon className="transition-transform group-hover:translate-x-0.5" aria-hidden />
            </>
          )}
        </Button>
        {back}
      </form>
    );
  }

  if (!otpMethodId) {
    return (
      <form onSubmit={startOtp} className="grid gap-5" noValidate>
        <FormError message={error} />
        <Field label={choice === 'sms' ? 'Mobile number' : 'Email address'} htmlFor="mfa-destination" description={choice === 'sms' ? 'We will text a code to this number each time you sign in.' : 'We will email a code to this address each time you sign in.'}>
          <Input
            id="mfa-destination"
            type={choice === 'sms' ? 'tel' : 'email'}
            inputMode={choice === 'sms' ? 'tel' : 'email'}
            autoComplete={choice === 'sms' ? 'tel' : 'email'}
            autoFocus
            placeholder={choice === 'sms' ? '(407) 555-0123' : 'you@company.com'}
            value={otpDestination}
            onChange={(e) => setOtpDestination(e.target.value)}
          />
        </Field>
        <Button type="submit" size="lg" className="group mt-1 w-full" disabled={busy || otpDestination.trim().length < 3}>
          {busy ? (
            <>
              <Loader2Icon className="animate-spin" aria-hidden /> Sending…
            </>
          ) : (
            <>
              Send me a code <ArrowRightIcon className="transition-transform group-hover:translate-x-0.5" aria-hidden />
            </>
          )}
        </Button>
        {back}
      </form>
    );
  }

  return (
    <form onSubmit={confirm} className="grid gap-5" noValidate>
      <div className="bg-muted/50 text-muted-foreground flex items-start gap-3 border p-3 text-sm">
        {choice === 'sms' ? <SmartphoneIcon className="mt-0.5 size-4 shrink-0" aria-hidden /> : <MailIcon className="mt-0.5 size-4 shrink-0" aria-hidden />}
        <span>
          We sent a 6-digit code to <span className="text-foreground font-medium">{sentTo}</span>.
        </span>
      </div>
      <FormError message={error} />
      <Field label="Verification code" htmlFor="mfa-enroll-code">
        <CodeInput id="mfa-enroll-code" value={code} onChange={setCode} />
      </Field>
      <RememberDevice days={mfa.rememberDeviceDays} checked={remember} onChange={setRemember} />
      <Button type="submit" size="lg" className="group mt-1 w-full" disabled={busy || code.trim().length < 6}>
        {busy ? (
          <>
            <Loader2Icon className="animate-spin" aria-hidden /> Checking…
          </>
        ) : (
          <>
            Turn on and sign in <ArrowRightIcon className="transition-transform group-hover:translate-x-0.5" aria-hidden />
          </>
        )}
      </Button>
      {back}
    </form>
  );
}
